import React from "react";
import { motion } from "framer-motion";
import { fadeIn, staggerContainer } from "../variants";
import { CheckCircle2, Users, BookOpen, Heart } from "lucide-react";
import EtablissementImg from "../img/about/etablissementAppe.jpg";
import EnfantsImg from "../img/about/enfantsCoursEcole.jpg";

const values = [
  {
    icon: <BookOpen size={20} />,
    title: "Education pour tous",
    desc: "Construire des écoles, former des enseignants, acheter du matériel pédagogique.",
  },
  {
    icon: <Heart size={20} />,
    title: "Petite enfance",
    desc: "Accueillir les mères adolescentes et leurs enfants en bas âge dans nos crèches.",
  },
  {
    icon: <Users size={20} />,
    title: "Émancipation des femmes",
    desc: "Formation professionnelle et alphabétisation pour les femmes et jeunes filles.",
  },
];

const objectives = [
  "Prendre en charge les mères adolescentes et leurs enfants",
  "Construire des bâtiments pédagogiques dans de bonnes conditions d'hygiène",
  "Assurer une prise en charge par des professionnels qualifiés",
  "Créer et entretenir des réseaux de partenariat internationaux",
  "Contribuer à l'amélioration des conditions de garde des enfants",
];

const bureau = [
  { role: "Présidente", name: "Toussaint HALTHODJIM" },
  { role: "Vice-Présidente", name: "Catherine TAMYAMBA" },
  { role: "Trésorière", name: "Chantal GODINEAU" },
  { role: "Trésorier Adjoint", name: "Fulbert MBAILLASSEM" },
  { role: "Secrétaire", name: "Tamian NINGAYO" },
  { role: "Secrétaire Adjointe", name: "Sabine TAMYAMBA" },
  { role: "Relations APPE/AME", name: "Jacob NGONGAR" },
  { role: "Événementiel", name: "Sylvie GODINEAU · Cendra NGONGAR" },
];

const About = () => {
  return (
    <section id="about" className="section scroll-mt-20">
      <div className="container mx-auto">

        {/* Section header */}
        <motion.div
          variants={staggerContainer(0.12, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="section-header"
        >
          <motion.p variants={fadeIn("up", 0)} className="eyebrow mb-4">
            Qui sommes-nous
          </motion.p>
          <motion.h2 variants={fadeIn("up", 0.05)} className="h2 mb-6">
            Une mission née du cœur, portée par l'espoir
          </motion.h2>
          <motion.p variants={fadeIn("up", 0.1)} className="lead">
            Fondée en 2004 à Bron par Catherine TAMYAMBA, l'APPE Tchad est née d'un constat simple :
            les jeunes mères de Sarh avaient besoin d'un lieu pour garder leurs enfants et construire leur avenir.
          </motion.p>
        </motion.div>

        {/* Story + image */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          <motion.div
            variants={fadeIn("right")}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            <span className="eyebrow mb-4 block">Notre histoire</span>
            <h3 className="h3 mb-6">Vingt ans d'engagement au Tchad</h3>
            <div className="space-y-4 text-grey leading-relaxed">
              <p>
                Catherine TAMYAMBA, née au Tchad près de Sarh, exerce en France comme auxiliaire de puériculture.
                Préoccupée par la situation des jeunes mamans de sa ville natale, elle crée l'APPE en 2004.
              </p>
              <p>
                En 2009, Toussaint HALTHODJIM la rejoint. Ensemble, ils relancent l'association.
                De 2010 à 2011, de nouveaux membres s'impliquent et l'AME (Association Mère Enfant) est créée à Sarh.
              </p>
              <p>
                En 2012, les premières subventions permettent de concrétiser le projet.
                En 2014, l'APPE est reconnue d'intérêt général, autorisant les reçus fiscaux pour les dons.
              </p>
            </div>

            {/* Values */}
            <div className="mt-10 space-y-4">
              {values.map((v, i) => (
                <div key={i} className="flex gap-4 p-4 rounded-xl bg-light hover:bg-primary/5 transition-colors">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center">
                    {v.icon}
                  </div>
                  <div>
                    <div className="font-semibold text-dark text-sm mb-1">{v.title}</div>
                    <div className="text-grey text-sm leading-relaxed">{v.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Images */}
          <motion.div
            variants={fadeIn("left")}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-card-hover">
              <img
                src={EtablissementImg}
                alt="Établissement APPE Sarh"
                className="w-full h-96 lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark/40 to-transparent" />
            </div>
            {/* Floating card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-card-hover border border-gray-100">
              <img
                src={EnfantsImg}
                alt="Enfants en cours"
                className="w-24 h-24 rounded-lg object-cover"
              />
              <div className="mt-2 text-xs font-semibold text-dark">École de Kemkian</div>
              <div className="text-xs text-grey">Sarh, Tchad</div>
            </div>
            {/* Year badge */}
            <div className="absolute top-4 right-4 bg-secondary text-white rounded-xl px-4 py-3 shadow-md text-center">
              <div className="font-display font-bold text-2xl">2004</div>
              <div className="text-white/80 text-xs">Fondée</div>
            </div>
          </motion.div>
        </div>

        {/* Objectives */}
        <motion.div
          variants={staggerContainer(0.1, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="grid lg:grid-cols-2 gap-16 items-start mb-24"
        >
          <motion.div variants={fadeIn("up")}>
            <span className="eyebrow mb-4 block">Nos objectifs</span>
            <h3 className="h3 mb-8">Ce que nous faisons concrètement</h3>
            <div className="space-y-3">
              {objectives.map((obj, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <CheckCircle2 size={18} className="text-accent flex-shrink-0 mt-0.5" />
                  <span className="text-grey text-sm leading-relaxed">{obj}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Bureau */}
          <motion.div variants={fadeIn("up", 0.1)}>
            <span className="eyebrow mb-4 block">Gouvernance</span>
            <h3 className="h3 mb-8">Bureau de l'association</h3>
            <p className="text-grey text-sm mb-6">
              Élus lors de l'AG du 21/03/2021 et ré-élus lors de l'AG du 07/05/2022
            </p>
            <div className="space-y-2">
              {bureau.map((m, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
                >
                  <span className="text-grey text-sm">{m.role}</span>
                  <span className="font-semibold text-dark text-sm">{m.name}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* AME partner */}
        <motion.div
          variants={fadeIn("up")}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="bg-primary/5 border border-primary/10 rounded-2xl p-8 lg:p-12"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="eyebrow mb-4 block">Partenaire local</span>
              <h3 className="h3 mb-4">Association Mère Enfant (AME)</h3>
              <p className="text-grey leading-relaxed mb-6">
                L'AME a été reconnue par la préfecture de Sarh en 2011. C'est l'antenne locale
                de l'APPE, opérationnelle depuis le début du projet. Elle supervise les activités
                sur le terrain et envoie chaque année des rapports et bilans.
              </p>
              <div className="flex flex-wrap gap-3">
                <div className="bg-white px-4 py-2 rounded-full text-sm text-dark font-medium shadow-sm border border-gray-100">
                  Reconnue en 2011
                </div>
                <div className="bg-white px-4 py-2 rounded-full text-sm text-dark font-medium shadow-sm border border-gray-100">
                  Quartier Kemkian, Sarh
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { role: "Présidente", name: "Soeur Rosalie NARADOUM" },
                { role: "Trésorier", name: "Olivier MADJADOUM" },
                { role: "Membre active", name: "Soeur MARIAM" },
                { role: "Membre active", name: "Louise NELDITA" },
              ].map((m, i) => (
                <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <div className="text-grey text-xs mb-1">{m.role}</div>
                  <div className="font-semibold text-dark text-sm">{m.name}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default About;
